/** Shared with the pre-React boot fallback. Keep this module dependency-free. */
export type RecoveryPageKind = 'not-found' | 'load-error';

export const RECOVERY_PAGE_CSS = `
body:has(.borrah-recovery){margin:0}
.borrah-recovery{box-sizing:border-box;min-height:100vh;min-height:100svh;display:grid;place-items:center;padding:48px 24px;padding-top:max(48px,env(safe-area-inset-top));padding-bottom:max(48px,env(safe-area-inset-bottom));background:var(--background,#fafaf9);color:var(--foreground,#252522);font-family:'Satoshi','Helvetica Neue',Helvetica,Arial,system-ui,sans-serif;text-align:center}
.borrah-recovery *{box-sizing:border-box}
.borrah-recovery__content{width:100%;max-width:440px}
.borrah-recovery__art{height:160px;display:flex;align-items:center;justify-content:center;gap:18px;margin:0 0 32px;user-select:none}
.borrah-recovery__digit{font-size:96px;font-weight:600;line-height:1;letter-spacing:-.04em;color:var(--muted-foreground,#6f6e69)}
.borrah-recovery__logo{position:relative;display:block;width:88px;height:88px;flex:none;isolation:isolate;transform:rotate(-8deg)}
.borrah-recovery__logo:before,.borrah-recovery__logo:after{content:'';position:absolute;inset:0;border-radius:20px;z-index:-1}
.borrah-recovery__logo:before{background:var(--border,#e4e4e1);transform:translate(6px,7px) rotate(17deg)}
.borrah-recovery__logo:after{background:var(--card,#fff);border:1px solid var(--border,#e4e4e1);transform:translate(-5px,-5px) rotate(-9deg)}
.borrah-recovery__logo img{display:block;width:88px;height:88px;border-radius:20px;box-shadow:0 7px 18px rgba(20,39,83,.12)}
.borrah-recovery__title{margin:0;font-size:30px;line-height:1.2;font-weight:600;letter-spacing:-.03em;text-wrap:balance}
.borrah-recovery__description{max-width:36ch;margin:14px auto 0;font-size:16px;line-height:1.6;color:var(--muted-foreground,#6f6e69);text-wrap:pretty}
.borrah-recovery__actions{display:flex;flex-wrap:wrap;justify-content:center;align-items:center;gap:12px 20px;margin-top:28px}
.borrah-recovery__primary,.borrah-recovery__home{font:inherit;font-size:14px;font-weight:500;line-height:20px;text-decoration:none;-webkit-tap-highlight-color:transparent}
.borrah-recovery__primary{display:inline-flex;min-height:44px;align-items:center;justify-content:center;padding:11px 20px;border:0;border-radius:10px;background:var(--primary,#255ff6);color:var(--primary-foreground,#fff);cursor:pointer;transition:background-color 160ms ease-out,transform 160ms ease-out}
.borrah-recovery__primary:hover{background:color-mix(in srgb,var(--primary,#255ff6) 88%,#000)}
.borrah-recovery__primary:active{transform:translateY(1px)}
.borrah-recovery__home{display:inline-flex;min-height:44px;align-items:center;color:var(--foreground,#252522);text-underline-offset:4px}
.borrah-recovery__home:hover{text-decoration:underline}
.borrah-recovery__primary:focus-visible,.borrah-recovery__home:focus-visible{outline:2px solid var(--primary,#255ff6);outline-offset:4px}
.borrah-recovery ::selection{background:var(--primary,#255ff6);color:var(--primary-foreground,#fff)}
@media(max-width:380px){.borrah-recovery{padding-inline:20px}.borrah-recovery__art{gap:14px}.borrah-recovery__digit{font-size:80px}.borrah-recovery__title{font-size:26px}}
@media(prefers-reduced-motion:reduce){.borrah-recovery__primary{transition:none}.borrah-recovery__primary:active{transform:none}}
`;

/** Only fixed product copy enters this HTML. Never pass error details here. */
export function recoveryPageHtml(kind: RecoveryPageKind): string {
  const notFound = kind === 'not-found';
  const title = notFound ? 'Page not found' : 'We could not load this page';
  const description = notFound
    ? 'That page may have moved, or the link may be out of date.'
    : 'Please try again, or head back to Ask AI.';

  return `<style>${RECOVERY_PAGE_CSS}</style>
<main class="borrah-recovery" data-recovery-page="${notFound ? 'not-found' : 'load-error'}">
  <div class="borrah-recovery__content">
    <div class="borrah-recovery__art" aria-hidden="true">
      ${notFound ? '<span class="borrah-recovery__digit">4</span>' : ''}
      <span class="borrah-recovery__logo"><img src="/logo-icon.svg" alt="" width="88" height="88"></span>
      ${notFound ? '<span class="borrah-recovery__digit">4</span>' : ''}
    </div>
    <h1 class="borrah-recovery__title">${title}</h1>
    <p class="borrah-recovery__description"${notFound ? '' : ' role="alert"'}>${description}</p>
    <div class="borrah-recovery__actions">
      ${notFound ? '' : '<button class="borrah-recovery__primary" type="button" data-recovery-retry>Try again</button>'}
      <a class="${notFound ? 'borrah-recovery__primary' : 'borrah-recovery__home'}" href="/dashboard">Back to Ask AI</a>
    </div>
  </div>
</main>`;
}

/** Also works when the router, React entry, or app styles could not load. */
export function mountRecoveryPage(root: HTMLElement, onRetry: () => void) {
  root.innerHTML = recoveryPageHtml('load-error');
  root
    .querySelector<HTMLButtonElement>('[data-recovery-retry]')
    ?.addEventListener('click', onRetry);
}
